"use client";

import { c as _c } from "react/compiler-runtime";
import { Activity, Blocks, BookOpen, Bot, Boxes, Building2, Calculator, ChevronDown, Clock, Code, GitCompare, Gift, KeyRound, LayoutGrid, Menu, MessagesSquare, Network, Newspaper, ScrollText, Server, Shield, ShieldCheck, Wrench, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { LanguageSwitcher } from "@/components/language-switcher";
import { AuthLink } from "@/components/shared/auth-link";
import { ModelSearch } from "@/components/shared/model-search";
import { useUser } from "@/hooks/useUser";
import { Button } from "@/lib/components/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/lib/components/navigation-menu";
import { useAppConfig } from "@/lib/config";
import Logo from "@/lib/icons/Logo";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { ThemeToggle } from "./theme-toggle";
import type { ApiModel, ApiProvider } from "@/lib/fetch-models";
import type { Route } from "next";
function IconMenuItem(t0) {
  const $ = _c(28);
  const {
    title,
    href,
    description,
    icon: IconComponent,
    gradient,
    external
  } = t0;
  let t1;
  if ($[0] !== gradient) {
    t1 = cn("group/product flex items-start gap-3 select-none rounded-lg p-3 no-underline outline-none transition-all duration-300 bg-linear-to-br from-transparent to-transparent", gradient, "hover:shadow-lg focus:shadow-md");
    $[0] = gradient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkClassName = t1;
  let T0;
  let t2;
  let t3;
  if ($[2] !== IconComponent || $[3] !== gradient) {
    const iconColor = gradient.split(" ").slice(-2).join(" ");
    t3 = "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/80 transition-colors";
    T0 = IconComponent;
    t2 = cn("h-4 w-4 text-muted-foreground transition-colors", iconColor);
    $[2] = IconComponent;
    $[3] = gradient;
    $[4] = T0;
    $[5] = t2;
    $[6] = t3;
  } else {
    T0 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== T0 || $[8] !== t2) {
    t4 = <T0 className={t2} />;
    $[7] = T0;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== title) {
    t6 = <div className="text-sm font-medium leading-none">{title}</div>;
    $[13] = title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== description) {
    t7 = <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">{description}</p>;
    $[15] = description;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="space-y-0.5">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t5 || $[21] !== t8) {
    t9 = <>{t5}{t8}</>;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const inner = t9;
  let t10;
  if ($[23] !== external || $[24] !== href || $[25] !== inner || $[26] !== linkClassName) {
    t10 = <li><NavigationMenuLink asChild={true}>{external ? <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>{inner}</a> : <Link href={href as Route} prefetch={true} className={linkClassName}>{inner}</Link>}</NavigationMenuLink></li>;
    $[23] = external;
    $[24] = href;
    $[25] = inner;
    $[26] = linkClassName;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
export const Navbar = t0 => {
  const $ = _c(85);
  const {
    children,
    sticky: t1,
    models,
    providers
  } = t0;
  const sticky = t1 === undefined ? true : t1;
  const {
    t
  } = useI18n();
  const config = useAppConfig();
  const {
    user,
    isLoading
  } = useUser();
  const [hasMounted, setHasMounted] = useState(false);
  const isAuthenticated = hasMounted && !!user && !isLoading;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHasMounted(true);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  const productsLinks = [{
    title: t("landing.nav.products.aiGateway.title"),
    href: "/features/unified-api-interface",
    description: t("landing.nav.products.aiGateway.description"),
    icon: Network,
    gradient: "hover:from-violet-500/20 hover:to-purple-600/30 hover:shadow-violet-500/10 group-hover/product:text-violet-500 dark:group-hover/product:text-violet-400"
  }, {
    title: "DevPass",
    href: "https://devpass.doteb.com",
    description: t("landing.nav.products.devPass.description"),
    icon: Code,
    gradient: "hover:from-indigo-500/20 hover:to-blue-600/30 hover:shadow-indigo-500/10 group-hover/product:text-indigo-500 dark:group-hover/product:text-indigo-400",
    external: true
  }, {
    title: t("landing.nav.products.chatPlayground.title"),
    href: config.playgroundUrl ?? "#",
    description: t("landing.nav.products.chatPlayground.description"),
    icon: MessagesSquare,
    gradient: "hover:from-blue-500/20 hover:to-cyan-600/30 hover:shadow-blue-500/10 group-hover/product:text-blue-500 dark:group-hover/product:text-blue-400",
    external: true
  }, {
    title: t("landing.nav.products.observability.title"),
    href: "/features/performance-monitoring",
    description: t("landing.nav.products.observability.description"),
    icon: Activity,
    gradient: "hover:from-emerald-500/20 hover:to-teal-600/30 hover:shadow-emerald-500/10 group-hover/product:text-emerald-500 dark:group-hover/product:text-emerald-400"
  }];
  const resourcesLinks = [{
    title: t("landing.nav.resources.enterprise.title"),
    href: "/enterprise",
    description: t("landing.nav.resources.enterprise.description"),
    icon: Building2,
    gradient: "hover:from-blue-500/20 hover:to-blue-600/30 hover:shadow-blue-500/10 group-hover/product:text-blue-500 dark:group-hover/product:text-blue-400"
  }, {
    title: t("landing.nav.resources.blog.title"),
    href: "/blog",
    description: t("landing.nav.resources.blog.description"),
    icon: Newspaper,
    gradient: "hover:from-amber-500/20 hover:to-orange-600/30 hover:shadow-amber-500/10 group-hover/product:text-amber-500 dark:group-hover/product:text-amber-400"
  }, {
    title: t("landing.nav.resources.changelog.title"),
    href: "/changelog",
    description: t("landing.nav.resources.changelog.description"),
    icon: ScrollText,
    gradient: "hover:from-violet-500/20 hover:to-purple-600/30 hover:shadow-violet-500/10 group-hover/product:text-violet-500 dark:group-hover/product:text-violet-400"
  }, {
    title: t("landing.nav.resources.integrations.title"),
    href: "/guides",
    description: t("landing.nav.resources.integrations.description"),
    icon: Blocks,
    gradient: "hover:from-indigo-500/20 hover:to-blue-600/30 hover:shadow-indigo-500/10 group-hover/product:text-indigo-500 dark:group-hover/product:text-indigo-400"
  }, {
    title: t("landing.nav.resources.reliability.title"),
    href: "/reliability",
    description: t("landing.nav.resources.reliability.description"),
    icon: ShieldCheck,
    gradient: "hover:from-emerald-500/20 hover:to-teal-600/30 hover:shadow-emerald-500/10 group-hover/product:text-emerald-500 dark:group-hover/product:text-emerald-400"
  }, {
    title: t("landing.nav.resources.guardrails.title"),
    href: "/features/guardrails",
    description: t("landing.nav.resources.guardrails.description"),
    icon: Shield,
    gradient: "hover:from-rose-500/20 hover:to-red-600/30 hover:shadow-rose-500/10 group-hover/product:text-rose-500 dark:group-hover/product:text-rose-400"
  }, {
    title: t("landing.nav.resources.providers.title"),
    href: "/providers",
    description: t("landing.nav.resources.providers.description"),
    icon: KeyRound,
    gradient: "hover:from-cyan-500/20 hover:to-blue-600/30 hover:shadow-cyan-500/10 group-hover/product:text-cyan-500 dark:group-hover/product:text-cyan-400"
  }, {
    title: t("landing.nav.resources.apps.title"),
    href: "/apps",
    description: t("landing.nav.resources.apps.description"),
    icon: LayoutGrid,
    gradient: "hover:from-pink-500/20 hover:to-rose-600/30 hover:shadow-pink-500/10 group-hover/product:text-pink-500 dark:group-hover/product:text-pink-400"
  }, {
    title: t("landing.nav.resources.models.title"),
    href: "/models",
    description: t("landing.nav.resources.models.description"),
    icon: Boxes,
    gradient: "hover:from-purple-500/20 hover:to-fuchsia-600/30 hover:shadow-purple-500/10 group-hover/product:text-purple-500 dark:group-hover/product:text-purple-400"
  }, {
    title: t("landing.nav.resources.modelTimeline.title"),
    href: "/timeline",
    description: t("landing.nav.resources.modelTimeline.description"),
    icon: Clock,
    gradient: "hover:from-teal-500/20 hover:to-cyan-600/30 hover:shadow-teal-500/10 group-hover/product:text-teal-500 dark:group-hover/product:text-teal-400"
  }, {
    title: t("landing.nav.resources.compare.title"),
    href: "/models/compare",
    description: t("landing.nav.resources.compare.description"),
    icon: GitCompare,
    gradient: "hover:from-sky-500/20 hover:to-blue-600/30 hover:shadow-sky-500/10 group-hover/product:text-sky-500 dark:group-hover/product:text-sky-400"
  }, {
    title: t("landing.nav.resources.tokenCostCalculator.title"),
    href: "/token-cost-calculator",
    description: t("landing.nav.resources.tokenCostCalculator.description"),
    icon: Calculator,
    gradient: "hover:from-green-500/20 hover:to-emerald-600/30 hover:shadow-green-500/10 group-hover/product:text-green-500 dark:group-hover/product:text-green-400"
  }, {
    title: t("landing.nav.resources.referralProgram.title"),
    href: "/referrals",
    description: t("landing.nav.resources.referralProgram.description"),
    icon: Gift,
    gradient: "hover:from-yellow-500/20 hover:to-amber-600/30 hover:shadow-yellow-500/10 group-hover/product:text-yellow-500 dark:group-hover/product:text-yellow-400"
  }];
  const aiLinks = [{
    title: "MCP Server",
    href: "/mcp",
    description: t("landing.nav.ai.mcp.description"),
    icon: Server,
    gradient: "hover:from-cyan-500/20 hover:to-blue-600/30 hover:shadow-cyan-500/10 group-hover/product:text-cyan-500 dark:group-hover/product:text-cyan-400"
  }, {
    title: t("landing.nav.ai.agents.title"),
    href: "/agents",
    description: t("landing.nav.ai.agents.description"),
    icon: Bot,
    gradient: "hover:from-violet-500/20 hover:to-purple-600/30 hover:shadow-violet-500/10 group-hover/product:text-violet-500 dark:group-hover/product:text-violet-400"
  }, {
    title: t("landing.nav.ai.templates.title"),
    href: "/templates",
    description: t("landing.nav.ai.templates.description"),
    icon: Wrench,
    gradient: "hover:from-emerald-500/20 hover:to-teal-600/30 hover:shadow-emerald-500/10 group-hover/product:text-emerald-500 dark:group-hover/product:text-emerald-400"
  }, {
    title: t("landing.nav.ai.guides.title"),
    href: "/guides",
    description: t("landing.nav.ai.guides.description"),
    icon: BookOpen,
    gradient: "hover:from-blue-500/20 hover:to-indigo-600/30 hover:shadow-blue-500/10 group-hover/product:text-blue-500 dark:group-hover/product:text-blue-400"
  }];
  const mobileSections = [{
    label: t("landing.nav.productsLabel"),
    items: productsLinks.map(_temp)
  }, {
    label: t("landing.nav.resourcesLabel"),
    items: resourcesLinks.map(_temp2)
  }, {
    label: "AI",
    items: aiLinks.map(_temp3)
  }];
  const [menuState, setMenuState] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  const t6 = sticky && "fixed";
  let t7;
  if ($[4] !== t6) {
    t7 = cn("z-20 w-full px-2 group", t6);
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = isScrolled && "bg-background/50 max-w-[1400px] rounded-2xl border backdrop-blur-lg lg:px-5";
  let t9;
  if ($[6] !== t8) {
    t9 = cn("mt-2 mx-auto max-w-[1400px] px-6 transition-all duration-300", t8);
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t) {
    t10 = t("landing.nav.home");
    $[8] = t;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Logo className="h-8 w-8 rounded-full text-black dark:text-white" />;
    t12 = <span className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white">doteb</span>;
    $[10] = t11;
    $[11] = t12;
  } else {
    t11 = $[10];
    t12 = $[11];
  }
  let t13;
  if ($[12] !== t10) {
    t13 = <Link href="/" aria-label={t10} className="flex items-center space-x-2" prefetch={true}>{t11}{t12}</Link>;
    $[12] = t10;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== menuState) {
    t14 = () => setMenuState(!menuState);
    $[14] = menuState;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== menuState || $[17] !== t) {
    t15 = menuState ? t("landing.nav.closeMenu") : t("landing.nav.openMenu");
    $[16] = menuState;
    $[17] = t;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Menu className="group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0 size-6 duration-200" />;
    t17 = <X className="absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100 duration-200" />;
    $[19] = t16;
    $[20] = t17;
  } else {
    t16 = $[19];
    t17 = $[20];
  }
  let t18;
  if ($[21] !== t14 || $[22] !== t15) {
    t18 = <button onClick={t14} aria-label={t15} className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 nav:hidden">{t16}{t17}</button>;
    $[21] = t14;
    $[22] = t15;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t13 || $[25] !== t18) {
    t19 = <div className="flex w-full justify-between nav:w-auto">{t13}{t18}</div>;
    $[24] = t13;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== models || $[28] !== providers) {
    t20 = <div className="w-[140px] lg:w-[160px]"><ModelSearch models={models} providers={providers} /></div>;
    $[27] = models;
    $[28] = providers;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t) {
    t21 = t("landing.nav.productsLabel");
    $[30] = t;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t21) {
    t22 = <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-4 py-2 bg-transparent">{t21}</NavigationMenuTrigger>;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t) {
    t23 = t("landing.nav.resourcesLabel");
    $[34] = t;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t23) {
    t24 = <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-4 py-2 bg-transparent">{t23}</NavigationMenuTrigger>;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const t25 = config.docsUrl ?? "";
  let t26;
  if ($[38] !== t) {
    t26 = t("landing.nav.docs");
    $[38] = t;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t25 || $[41] !== t26) {
    t27 = <NavigationMenuItem><NavigationMenuLink asChild={true}><a href={t25} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground block duration-150 px-4 py-2">{t26}</a></NavigationMenuLink></NavigationMenuItem>;
    $[40] = t25;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-4 py-2 bg-transparent">AI</NavigationMenuTrigger>;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t) {
    t29 = t("landing.nav.pricing");
    $[44] = t;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== t29) {
    t30 = <NavigationMenuItem><NavigationMenuLink asChild={true}><Link href="/pricing" prefetch={true} className="text-muted-foreground hover:text-accent-foreground block duration-150 px-4 py-2">{t29}</Link></NavigationMenuLink></NavigationMenuItem>;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  const t31 = "bg-background group-data-[state=active]:block nav:group-data-[state=active]:flex mb-6 hidden max-h-[calc(100dvh-7rem)] w-full flex-wrap items-center justify-end space-y-6 overflow-y-auto overscroll-contain rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap nav:m-0 nav:flex nav:max-h-none nav:w-fit nav:shrink-0 nav:gap-3 nav:space-y-0 nav:overflow-visible nav:border-transparent nav:bg-transparent nav:p-0 nav:shadow-none dark:shadow-none dark:nav:bg-transparent";
  const t32 = "nav:hidden";
  let t33;
  if ($[48] !== models || $[49] !== providers) {
    t33 = <div className="mb-4"><ModelSearch models={models} providers={providers} /></div>;
    $[48] = models;
    $[49] = providers;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  const t34 = "text-base";
  let t35;
  if ($[51] !== t) {
    t35 = t("landing.nav.pricing");
    $[51] = t;
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  let t36;
  if ($[53] !== t35) {
    t36 = <li><Link href="/pricing" className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150" prefetch={true}>{t35}</Link></li>;
    $[53] = t35;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  const t37 = config.docsUrl ?? "";
  let t38;
  if ($[55] !== t) {
    t38 = t("landing.nav.docs");
    $[55] = t;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  if ($[57] !== t37 || $[58] !== t38) {
    t39 = <li><a href={t37} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150">{t38}</a></li>;
    $[57] = t37;
    $[58] = t38;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  if ($[60] !== t) {
    t40 = t("landing.nav.models");
    $[60] = t;
    $[61] = t40;
  } else {
    t40 = $[61];
  }
  let t41;
  if ($[62] !== t40) {
    t41 = <li><Link href="/models" className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150" prefetch={true}>{t40}</Link></li>;
    $[62] = t40;
    $[63] = t41;
  } else {
    t41 = $[63];
  }
  const t42 = mobileSections.map(section => <li key={section.label}><button type="button" onClick={() => setOpenMobileSection(openMobileSection === section.label ? null : section.label)} className="flex w-full items-center justify-between gap-2 py-2.5 text-left" aria-expanded={openMobileSection === section.label}><span className="text-muted-foreground">{section.label}</span><ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", openMobileSection === section.label && "rotate-180")} /></button><ul className={cn("grid grid-cols-2 gap-x-4 rounded-xl bg-muted/40 px-3 py-2 mb-2", openMobileSection !== section.label && "hidden")}>{section.items.map(_temp7)}</ul></li>);
  let t43;
  if ($[64] !== t36 || $[65] !== t39 || $[66] !== t41 || $[67] !== t42) {
    t43 = <ul className={t34}>{t36}{t39}{t41}{t42}</ul>;
    $[64] = t36;
    $[65] = t39;
    $[66] = t41;
    $[67] = t42;
    $[68] = t43;
  } else {
    t43 = $[68];
  }
  let t44;
  if ($[69] !== t33 || $[70] !== t43) {
    t44 = <div className={t32}>{t33}{t43}</div>;
    $[69] = t33;
    $[70] = t43;
    $[71] = t44;
  } else {
    t44 = $[71];
  }
  let t45;
  if ($[72] !== children) {
    t45 = <div className="hidden nav:flex items-center gap-1">{children}</div>;
    $[72] = children;
    $[73] = t45;
  } else {
    t45 = $[73];
  }
  let t46;
  let t47;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <ThemeToggle />;
    t47 = <LanguageSwitcher compact={true} />;
    $[74] = t46;
    $[75] = t47;
  } else {
    t46 = $[74];
    t47 = $[75];
  }
  let t48;
  if ($[76] !== isAuthenticated || $[77] !== t) {
    t48 = isAuthenticated ? <Button asChild={true} className="bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-700 dark:hover:bg-zinc-200 font-medium w-full md:w-fit"><Link href="/dashboard" prefetch={true}>{t("landing.nav.dashboard")}</Link></Button> : <><Link href="/login" prefetch={true} className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden nav:block whitespace-nowrap">{t("landing.nav.login")}</Link><Button asChild={true} className="bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-700 dark:hover:bg-zinc-200 font-medium w-full md:w-fit"><AuthLink href="/signup">{t("landing.nav.getStarted")}</AuthLink></Button></>;
    $[76] = isAuthenticated;
    $[77] = t;
    $[78] = t48;
  } else {
    t48 = $[78];
  }
  let t49;
  if ($[79] !== t45 || $[80] !== t48) {
    t49 = <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit items-center">{t45}{t46}{t47}{t48}</div>;
    $[79] = t45;
    $[80] = t48;
    $[81] = t49;
  } else {
    t49 = $[81];
  }
  let t50;
  if ($[82] !== t44 || $[83] !== t49) {
    t50 = <div className={t31}>{t44}{t49}</div>;
    $[82] = t44;
    $[83] = t49;
    $[84] = t50;
  } else {
    t50 = $[84];
  }
  return <header><nav data-state={menuState && "active"} className={t7}><div className={t9}><div className="relative flex flex-wrap items-center justify-between gap-6 py-3 nav:flex-nowrap nav:gap-0 nav:py-4">{t19}<div className="m-auto hidden items-center gap-2 nav:flex min-w-0">{t20}<NavigationMenu viewport={false} delayDuration={300}><NavigationMenuList className="flex gap-1 text-sm"><NavigationMenuItem>{t22}<NavigationMenuContent><ul className="grid grid-cols-2 gap-2 p-4 md:w-[520px] lg:w-[580px]">{productsLinks.map(_temp4)}</ul></NavigationMenuContent></NavigationMenuItem><NavigationMenuItem>{t24}<NavigationMenuContent><ul className="grid grid-cols-2 gap-2 p-4 md:w-[680px] lg:w-[820px] lg:grid-cols-3">{resourcesLinks.map(_temp5)}</ul></NavigationMenuContent></NavigationMenuItem>{t27}<NavigationMenuItem>{t28}<NavigationMenuContent><ul className="grid grid-cols-2 gap-2 p-4 md:w-[520px] lg:w-[580px]">{aiLinks.map(_temp6)}</ul></NavigationMenuContent></NavigationMenuItem>{t30}</NavigationMenuList></NavigationMenu></div>{t50}</div></div></nav></header>;
};
function _temp(i) {
  return {
    name: i.title,
    href: i.href,
    external: i.external
  };
}
function _temp2(i_0) {
  return {
    name: i_0.title,
    href: i_0.href,
    external: i_0.external
  };
}
function _temp3(i_1) {
  return {
    name: i_1.title,
    href: i_1.href,
    external: i_1.external
  };
}
function _temp4(product) {
  return <IconMenuItem key={product.title} title={product.title} href={product.href} description={product.description} icon={product.icon} gradient={product.gradient} external={product.external} />;
}
function _temp5(link) {
  return <IconMenuItem key={link.title} title={link.title} href={link.href} description={link.description} icon={link.icon} gradient={link.gradient} external={link.external} />;
}
function _temp6(item) {
  return <IconMenuItem key={item.title} title={item.title} href={item.href} description={item.description} icon={item.icon} gradient={item.gradient} external={item.external} />;
}
function _temp7(item_0) {
  return <li key={item_0.name}>{item_0.external ? <a href={item_0.href} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground block py-2 duration-150 text-sm">{item_0.name}</a> : <Link href={item_0.href as Route} className="text-muted-foreground hover:text-accent-foreground block py-2 duration-150 text-sm" prefetch={true}>{item_0.name}</Link>}</li>;
}